/**
 * Store for `DATA.actions`
 * @module
 */

var _ = require('optimizely-client-fn');
var actionTypes = require('../constants/action_types');
var freeze = require('../utils/freeze');
var Logger = require('../utils/logger');

module.exports = {
  initialize: function() {
    this.__state = {
      // Actions by actionId
      // "Pure" action data from the initial LOAD_DATA call
      actions: {},

      /**
       * State of an action by Id
       * Provides state for an action while it is applying
       *
       * Structure:
       *
       *   actionId1: {
       *     changeId1: 'applier|null'
       *     asyncChange2: 'applier|null'
       *   }
       */
      actionState: {}
    };
    this.on(actionTypes.DATA_LOADED, this.__loadData);
    this.on(actionTypes.ACTION_EXECUTED, this.__initializeActionState);
    this.on(actionTypes.SET_CHANGE_APPLIER, this.__setChangeApplier);
    this.on(actionTypes.REMOVE_ACTION_STATE, this.__removeActionState);
  },

  /**
   * Loads action data into the store state
   * @param {Object} payload
   * @param {ClientData} payload.data
   * @private
   */
  __loadData: function(payload:any) {
    var self = this;
    if (_.isEmpty(payload.data.layers)) {
      return;
    }
    _.each(payload.data.layers, function (layer) {
      var actionData;
      if (layer.changes) {
        var layerActionId = 'layerId:' + layer.id;
        actionData = {
          id: layerActionId,
          layerId: layer.id,
          changeSet: layer.changes,
          type: 'layer',
        };
        freeze.deepFreeze(actionData);
        self.__state.actions[layerActionId] = actionData;
      }
      _.each(layer.experiments, function (experiment) {
        if (experiment.changes) {
          var experimentActionId = 'experimentId:' + experiment.id;
          actionData = {
            id: experimentActionId,
            layerId: layer.id,
            experimentId: experiment.id,
            changeSet: experiment.changes,
            type: 'experiment',
          };
          freeze.deepFreeze(actionData);
          self.__state.actions[experimentActionId] = actionData;
        }
        _.each(experiment.variations, function (variation) {
          _.each(variation.actions, function (action) {
            var pageId = action.pageId || action.viewId;
            var actionId = experiment.id + ':' + variation.id + ':' + pageId;
            actionData = {
              id: actionId,
              layerId: layer.id,
              experimentId: experiment.id,
              variationId: variation.id,
              pageId: pageId,
              changeSet: action.changes,
              type: 'variation',
            };
            freeze.deepFreeze(actionData);
            self.__state.actions[actionId] = actionData;
          });
        });
      });
    });
    this.emitChange();
  },

  /**
   * Set the initial state of an executed action in the actionState store.
   * @param {Object} payload
   * @param {string} payload.actionId
   * @private
   */
  __initializeActionState: function(payload:any) {
    var actionId = payload.actionId;
    if (!_.isUndefined(actionId) && !this.__state.actionState[actionId]) {
      this.__state.actionState[actionId] = {} as any;
    }
  },

  /**
   * Set the change applier instance for a change in the actionState store,
   * but only if the key for its action is already in the store (action is initialized).
   * @param {Object} payload
   * @param {string} payload.actionId
   * @param {string} payload.changeId
   * @param {ChangeApplier} payload.changeApplier
   * @private
   */
  __setChangeApplier: function(payload:any) {
    var actionId = payload.actionId;
    var changeId = payload.changeId;
    if (!this.__state.actionState[actionId]) {
      Logger.warn('Action Data / Attempted to set changeApplier for inactive action: ', actionId);
      return;
    }

    this.__state.actionState[actionId][changeId] = payload.changeApplier;
  },

  /**
   * Remove the stored state for a given action
   * @param {Object} payload
   * @param {string} payload.actionId
   * @private
   */
  __removeActionState: function(payload:any) {
    delete this.__state.actionState[payload.actionId];
  },

  /**
   * Retrieve a single action's data from the store
   * @param {string} id
   * @returns {ActionData}
   */
  get: function(id:string): ActionData {
    return freeze.safeReference(this.__state.actions[id]);
  },

  /**
   * Retrieve a single action's state from the store
   * @param {string} actionId
   * @returns {ActionData}
   */
  getActionState: function(actionId:string): ActionData {
    return freeze.safeReference(this.__state.actionState[actionId]);
  },

  /**
   * Get the action which contains the requested changeId in its changeSet
   * @param {string} changeId
   * @returns {ActionData|null}
   */
  getByChangeId: function(changeId:string): ActionData|null {
    return _.find(this.__state.actions, {
      changeSet: [
        {
          id: changeId,
        }
      ]
    });
  },

  /**
   * Retrieve a list of actions which apply to the specified pageId.
   * @param {string} pageId
   * @return {string[]}
   */
  getAllActionIdsByPageId: function(pageId:string): string[] {
    return _.map(_.filter(this.__state.actions, {
      pageId: pageId
    }), 'id');
  },

  /**
   * Get the changeApplier instance for the changeId/actionId specified.
   * @param {string} changeId
   * @param {string} actionId
   * @returns {ChangeApplier|undefined}
   */
  getChangeApplier: function(changeId:string, actionId:string): ChangeApplier|undefined {
    var actionState = this.__state.actionState[actionId];
    if (actionState) {
      return actionState[changeId];
    }
  },

  /**
   * Retrieve all actions for a variation given an experimentId and a variationId
   * @param {string} experimentId
   * @param {string} variationId
   * @returns {ActionData[]}
   */
  getExperimentVariationActions: function (experimentId:string, variationId:string): ActionData[] {
    return freeze.safeReference(_.filter(this.__state.actions, {
      experimentId: experimentId,
      variationId: variationId,
    }));
  },

  /**
   * Retrieve all actions for a given LayerId (not associated with variations)
   * @param {string} layerId
   * @returns {ActionData[]}
   */
  getLayerActions: function (layerId:string): ActionData[] {
    return freeze.safeReference(_.filter(this.__state.actions, {
      id: 'layerId:' + layerId,
    }));
  },

  /**
   * Retrieve all actions for a given LayerId (not associated with variations)
   * @param {string} experimentId
   * @returns {ActionData[]}
   */
  getExperimentActions: function (experimentId:string): ActionData[] {
    return freeze.safeReference(_.filter(this.__state.actions, {
      id: 'experimentId:' + experimentId,
    }));
  },

  /**
   * Retrieve all action data in store
   * @returns {ActionData[]}
   */
  getAll: function(): ActionData[] {
    return freeze.safeReference(_.values(this.__state.actions));
  },
};
